// ═══════════════════════════════════════════════════════════════
// Výběr obrázku z lokálního disku (sekce 2.3 Ilustrace / 2.4c FSA).
//
// Primárně File System Access API (`showOpenFilePicker`), cíl je Chrome.
// Fallback na skryté <input type="file"> pro prohlížeče/kontexty, kde FSA
// není (např. iframe bez povolení). Soubor se načte jako data URL (base64),
// aby šel přímo do `src` obrázkového uzlu — viz src/editor/image.ts.
// ═══════════════════════════════════════════════════════════════

const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/bmp,image/svg+xml';

export interface PickedImage {
  dataUrl: string;
  width: number;
  height: number;
  name: string;
}

/** Otevře dialog pro výběr obrázku a vrátí data URL + přirozené rozměry. */
export async function pickImage(): Promise<PickedImage | null> {
  const file = await chooseFile();
  if (!file) return null;
  return await readImage(file);
}

async function chooseFile(): Promise<File | null> {
  const anyWin = window as any;
  if (typeof anyWin.showOpenFilePicker === 'function') {
    try {
      const [handle] = await anyWin.showOpenFilePicker({
        multiple: false,
        types: [
          {
            description: 'Obrázky',
            accept: {
              'image/png': ['.png'],
              'image/jpeg': ['.jpg', '.jpeg'],
              'image/gif': ['.gif'],
              'image/webp': ['.webp'],
              'image/bmp': ['.bmp'],
              'image/svg+xml': ['.svg'],
            },
          },
        ],
      });
      return await handle.getFile();
    } catch (err: any) {
      // AbortError = uživatel zrušil dialog → tichý návrat.
      if (err?.name === 'AbortError') return null;
      // jiná chyba → zkus fallback
    }
  }
  return await inputFallback();
}

function inputFallback(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPT;
    input.style.display = 'none';
    input.addEventListener('change', () => {
      const f = input.files && input.files[0] ? input.files[0] : null;
      input.remove();
      resolve(f);
    });
    // Když uživatel zavře dialog bez výběru, change nepřijde — po focusu
    // zpět bez souboru to po chvíli uklidíme.
    window.addEventListener(
      'focus',
      () => setTimeout(() => { if (input.isConnected && (!input.files || !input.files.length)) { input.remove(); resolve(null); } }, 500),
      { once: true },
    );
    document.body.appendChild(input);
    input.click();
  });
}

/** Načte File jako data URL a zjistí přirozené rozměry obrázku. */
export function readImage(file: File): Promise<PickedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Nepodařilo se načíst soubor.'));
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const probe = new window.Image();
      probe.onload = () =>
        resolve({ dataUrl, width: probe.naturalWidth, height: probe.naturalHeight, name: file.name });
      probe.onerror = () =>
        // SVG bez explicitních rozměrů nemusí naturalWidth vrátit — dovol i tak.
        resolve({ dataUrl, width: 0, height: 0, name: file.name });
      probe.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Výchozí velikost při vložení: obrázek se vejde do šířky stránky (mm→px při
 * ~96 dpi je hrubé, ale pro editor stačí). Pokud je menší, ponech přirozenou.
 */
export function initialSize(natW: number, natH: number, maxContentPx = 640): { width: number; height: number } | null {
  if (!natW || !natH) return null;
  if (natW <= maxContentPx) return { width: natW, height: natH };
  const scale = maxContentPx / natW;
  return { width: Math.round(natW * scale), height: Math.round(natH * scale) };
}
